import { Link } from "react-router-dom";
import AnimatedArrow from "./AnimatedArrow";
import ImageSlider, { type SliderImage } from "./ImageSlider";
import useInView from "../hooks/useInView";
import useParallax from "../hooks/useParallax";
import { useLanguage } from "../context/LanguageContext";

// A single photo, not a carousel — ImageSlider degrades to a static banner
// (no clone-wrap, no autoplay, no transition) whenever it's handed exactly
// one image, so this still goes through the same component rather than a
// one-off <img>.
const CTA_BANNER_IMAGES: SliderImage[] = [
  { id: "cta-banner-plating", image: "/assets/posters/cta-banner-plating.jpg", width: 2000, height: 1432 },
];

export default function CtaBanner({ className = "" }: { className?: string }) {
  const { t } = useLanguage();
  // Scaled up (scale-125) so a 24px max scroll-driven offset never reveals
  // the slider's own edge — same overflow-buffer trick as PortfolioCard.
  const { ref: parallaxRef, offset } = useParallax<HTMLDivElement>(0.04, 24);
  // Heading, description and the button row reveal as three separate
  // staggered beats (same transition-on-class-toggle technique as
  // StatsSection's AnimatedStat/StatLabel) instead of the previous single
  // <Reveal> wrapper, which faded the whole block in as one flat unit —
  // easy to miss and not what "the buttons and text should animate" was
  // asking for.
  const { ref: revealRef, inView } = useInView<HTMLDivElement>(0.2);
  const revealClass = `transition-all duration-700 ease-engineered ${
    inView ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
  }`;
  const revealStyle = (delayMs: number) => (inView ? { transitionDelay: `${delayMs}ms` } : undefined);

  return (
    <section className={`relative -mb-px flex min-h-[520px] w-full items-center overflow-hidden bg-ink py-20 md:min-h-[680px] md:py-28 ${className}`}>
      <div
        ref={parallaxRef}
        className="absolute inset-0 will-change-transform"
        style={{ transform: `translateY(${offset}px)` }}
      >
        {/* interactive={false} — this is a decorative background sitting
            behind the section's own CTA buttons, not a banner the visitor
            browses directly, so a stray click shouldn't page it. */}
        <ImageSlider images={CTA_BANNER_IMAGES} interactive={false} className="scale-125" />
      </div>
      <div className="absolute inset-0 bg-black/40" />

      <div className="container-industrial relative z-10">
        <div
          ref={revealRef}
          className="flex max-w-2xl flex-col items-start gap-6 text-left sm:mx-auto sm:items-center sm:text-center"
        >
          <h2
            className={`font-display uppercase text-[40px] font-medium leading-[1.1] tracking-[0.3px] text-white md:text-[45px] ${revealClass}`}
            style={revealStyle(0)}
          >
            {t("ctaHeading")}
          </h2>
          <p
            className={`max-w-lg text-base leading-relaxed text-white/70 md:text-lg ${revealClass}`}
            style={revealStyle(150)}
          >
            {t("ctaDescription")}
          </p>
          <div
            className={`flex w-full flex-wrap items-center gap-4 sm:w-auto ${revealClass}`}
            style={revealStyle(300)}
          >
            <Link to="/contacts" className="btn-primary h-[52px] w-full shrink-0 sm:h-auto sm:w-auto">
              {t("ctaButton")}
              <AnimatedArrow />
            </Link>
            <Link to="/catalog" className="btn-outline h-[52px] w-full shrink-0 sm:h-auto sm:w-auto">
              {t("heroCtaPrimary")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
